import type { LinksFunction, MetaFunction } from "react-router";
import { Link, useMatches } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, FileCheck2, Grid2X2, Info, LogIn, UserPlus, type LucideIcon } from "lucide-react";
import { Banner } from "~/features/core/Banner";
import { PublicFooter } from "~/features/public/Footer";
import { NavBar } from "~/features/public/NavBar";
import CaboPocLogoPng from "~/assets/logos/new_logos/CaboPoc_Claim@4x.png";
import CaboLabLogoPng from "~/assets/logos/new_logos/CaboLab Claim@4x.png";
import CaboVaxLogoPng from "~/assets/logos/new_logos/CaboVax@4x_Claim.png";

export const meta: MetaFunction = () => [
  { title: "CaboCheck – Präventions-Diagnostik für Apotheken und Endkunden" },
  {
    name: "description",
    content:
      "CaboCheck verbindet Apotheken und Endkunden mit digitalen Lösungen für Labordiagnostik, Point-of-Care-Tests und Impfprozesse.",
  },
];

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700;12..96,800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500;600&display=swap",
  },
];

export default function Start() {
  const { t } = useTranslation();
  const matches = useMatches();
  const rootData = matches[0]?.data as { loggedIn?: boolean } | undefined;
  const loginTo = rootData?.loggedIn ? "/client/dashboard" : "/v1/auth/login";
  const right = (
    <div className="flex items-center gap-4">
      <Link
        to="https://mein.cabocheck.com"
        className="hidden rounded-lg px-1 py-2 text-sm font-medium text-slate-500 transition hover:text-slate-950 sm:inline-flex"
      >
        {t("Testergebnis abrufen")}
      </Link>
      <Link
        to={loginTo}
        className="inline-flex items-center justify-center rounded-xl bg-blue-550 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-800/20 transition hover:-translate-y-0.5 hover:bg-blue-900"
      >
        {t("Login")}
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen overflow-x-clip bg-public-home bg-fixed text-slate-950 antialiased">
      <Banner />
      <NavBar logoTo="/" navItems={[]} right={right} />
      <main>
        <HeroSection />
        <DoorSwitch loginTo={loginTo} />
        <ProductSection />
      </main>
      <PublicFooter />
    </div>
  );
}

function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="public-container pb-2 pt-8 text-center sm:pt-16 lg:pt-20">
      <p className="mb-4 font-mono-brand text-xs font-semibold uppercase tracking-widest text-slate-500 sm:mb-5">
        {t("Präventions-Diagnostik · Apotheken & Endkunden")}
      </p>
      <h1 className="mx-auto max-w-4xl font-display text-4xl font-extrabold leading-none tracking-tight sm:text-5xl lg:text-6xl">
        {t("Willkommen bei CaboCheck.")}
      </h1>
      <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-500 sm:mt-5 sm:text-lg">
        {t("Sie sind Apotheke oder Endkunde? Wählen Sie Ihren Bereich – wir bringen Sie direkt ans Ziel.")}
      </p>
    </section>
  );
}

function DoorSwitch({ loginTo }: { loginTo: string }) {
  const { t } = useTranslation();

  const customerRows = [
    {
      to: "https://mein.cabocheck.com",
      icon: FileCheck2,
      label: t("Testergebnis abrufen"),
    },
    {
      to: "/tests",
      icon: Info,
      label: t("Infos zum Angebot in Ihrer Apotheke"),
    },
  ];

  const pharmacyRows = [
    {
      to: "#produkte",
      icon: Grid2X2,
      label: t("Produkte entdecken"),
    },
    {
      to: loginTo,
      icon: LogIn,
      label: t("Als Apotheke anmelden (Login)"),
    },
    {
      to: "/register/pharmacy/step2",
      icon: UserPlus,
      label: t("Partner werden / Demo anfragen"),
    },
  ];

  return (
    <section className="public-container py-5 sm:py-8" aria-label={t("Bereich wählen")}>
      <div className="group grid items-stretch gap-4 sm:gap-5 lg:flex lg:gap-0">
        <DoorCard
          accent="red"
          eyebrow={t("Für Endkunden")}
          title={t("Ich bin Kunde")}
          description={t(
            "Sie haben in Ihrer Apotheke einen Test gemacht oder eine Impfung erhalten? Hier rufen Sie Ihre Ergebnisse und Unterlagen ab.",
          )}
          rows={customerRows}
          ctaTo="/tests"
          ctaLabel={t("Zum Kundenbereich")}
        />
        <div
          className="flex items-center justify-center gap-3 px-1 py-0 lg:flex-none lg:flex-col lg:px-5"
          aria-hidden="true"
        >
          <span className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent lg:h-auto lg:w-px lg:bg-gradient-to-b" />
          <span className="grid h-12 w-12 place-items-center rounded-full border border-slate-200 bg-white font-mono-brand text-xs font-semibold text-slate-500 shadow-lg shadow-slate-900/10">
            {t("oder")}
          </span>
          <span className="h-px flex-1 bg-gradient-to-l from-slate-200 to-transparent lg:h-auto lg:w-px lg:bg-gradient-to-t" />
        </div>
        <DoorCard
          accent="blue"
          eyebrow={t("Für Apotheken")}
          title={t("Ich bin Apotheke")}
          description={t(
            "Bieten Sie Labordiagnostik, Point-of-Care-Tests und Impfprozesse digital an – integriert in einer Plattform.",
          )}
          rows={pharmacyRows}
          ctaTo="#produkte"
          ctaLabel={t("Zum Apothekenbereich")}
        />
      </div>
    </section>
  );
}

function DoorCard({
  accent,
  eyebrow,
  title,
  description,
  rows,
  ctaTo,
  ctaLabel,
}: {
  accent: "red" | "blue";
  eyebrow: string;
  title: string;
  description: string;
  rows: { to: string; icon: LucideIcon; label: string }[];
  ctaTo: string;
  ctaLabel: string;
}) {
  const styles =
    accent === "red"
      ? {
          bar: "bg-brand-red",
          tag: "bg-brand-red-tint text-brand-red-dark",
          icon: "text-brand-red-dark",
          row: "hover:border-brand-red/40 hover:bg-brand-red-tint",
          arrow: "text-brand-red",
          cta: "bg-brand-red shadow-brand-red/20 hover:bg-brand-red-dark",
          border: "hover:border-brand-red/40 focus-within:border-brand-red/40",
        }
      : {
          bar: "bg-blue-550",
          tag: "bg-blue-250 text-blue-900",
          icon: "text-blue-900",
          row: "hover:border-blue-550/40 hover:bg-blue-250",
          arrow: "text-blue-550",
          cta: "bg-blue-550 shadow-blue-800/20 hover:bg-blue-900",
          border: "hover:border-blue-550/40 focus-within:border-blue-550/40",
        };

  return (
    <article
      className={`relative flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/10 transition duration-300 focus-within:!opacity-100 hover:-translate-y-2 hover:!opacity-100 hover:shadow-2xl group-hover:opacity-60 sm:p-8 lg:flex-1 ${styles.border}`}
    >
      <div className={`absolute inset-x-0 top-0 h-1.5 ${styles.bar}`} />
      <span
        className={`mb-4 inline-flex w-fit items-center gap-2 rounded-full px-3 py-2 font-mono-brand text-xs font-semibold uppercase tracking-widest sm:mb-5 ${styles.tag}`}
      >
        <span className={`h-2 w-2 rounded-full ${styles.bar}`} />
        {eyebrow}
      </span>
      <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{title}</h2>
      <p className="mt-3 max-w-md text-sm leading-6 text-slate-500">{description}</p>
      <ul className="my-5 grid gap-2.5 sm:my-7">
        {rows.map((row) => (
          <li key={`${row.to}-${row.label}`}>
            <ActionRow {...row} accentStyles={styles} />
          </li>
        ))}
      </ul>
      <Link
        to={ctaTo}
        className={`mt-auto inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 sm:py-3.5 ${styles.cta}`}
      >
        {ctaLabel}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

function ActionRow({
  to,
  icon: Icon,
  label,
  accentStyles,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  accentStyles: { icon: string; row: string; arrow: string };
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold transition hover:translate-x-1 sm:px-4 sm:py-3.5 ${accentStyles.row}`}
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-slate-200 bg-white">
        <Icon className={`h-4 w-4 ${accentStyles.icon}`} aria-hidden="true" />
      </span>
      <span>{label}</span>
      <ArrowRight
        className={`ml-auto h-4 w-4 transition group-hover:translate-x-1 ${accentStyles.arrow}`}
        aria-hidden="true"
      />
    </Link>
  );
}

function ProductSection() {
  const { t } = useTranslation();
  const products = [
    {
      title: "CaboLab",
      logo: CaboLabLogoPng,
      to: "/apotheke",
      description: t("Digitale Labordiagnostik für Ihre Apotheke – Proben, Befunde und Versand in einem Ablauf."),
    },
    {
      title: "CaboPOC",
      logo: CaboPocLogoPng,
      to: "/cabopoc",
      description: t("POC-Diagnostik. Schnell testen, Ergebnisse digital erfassen und rechtssicher verwalten."),
    },
    {
      title: "CaboVax",
      logo: CaboVaxLogoPng,
      to: "/cabovax",
      description: t("Digitale Impfprozesse. Aufklärung, Einwilligung und Dokumentation – einfach umgesetzt."),
    },
  ];

  return (
    <section id="produkte" className="public-container pb-14 pt-9 sm:pb-20 sm:pt-12">
      <div className="mb-7 text-center sm:mb-9">
        <p className="mb-3 font-mono-brand text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t("Unsere Lösungen für Apotheken")}
        </p>
        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
          {t("Bei diesen drei Themen unterstützen wir Apotheken")}
        </h2>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        {products.map((product) => (
          <ProductCard key={product.title} {...product} />
        ))}
      </div>
    </section>
  );
}

function ProductCard({
  title,
  logo,
  description,
  to,
}: {
  title: string;
  logo: string;
  description: string;
  to: string;
}) {
  const { t } = useTranslation();

  return (
    <article className="flex flex-col rounded-3xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/10 transition hover:-translate-y-1.5 hover:border-blue-550/30 hover:shadow-2xl sm:p-6">
      <img src={logo} alt={title} className="mb-5 h-20 w-fit max-w-full object-contain" />
      <p className="mb-6 min-h-12 text-sm leading-6 text-slate-500">{description}</p>
      <Link
        to={to}
        className="mt-auto inline-flex items-center gap-2 font-semibold text-blue-550 transition hover:gap-3 hover:text-blue-900"
      >
        {t("Mehr erfahren")}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </article>
  );
}
